import { useState, useEffect } from "react";
import { 
  Users, Plus, Search, Eye, Edit, Trash2, RefreshCw,
  Calendar, Clock, Mail, Phone, Briefcase, Send, FileEdit,
  CheckCircle, AlertCircle, XCircle, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const AdminInterviews = () => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendingInvitation, setSendingInvitation] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    interview_datetime: "",
    status: "scheduled",
    notes: ""
  });
  
  // Email template
  const [emailTemplate, setEmailTemplate] = useState({
    subject: "",
    body_html: ""
  });
  const [savingTemplate, setSavingTemplate] = useState(false);

  const getToken = () => localStorage.getItem("admin_token");

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getToken()}` }
  });

  useEffect(() => {
    fetchInterviews();
  }, []);

  const fetchInterviews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/interviews`, authHeaders());
      setInterviews(response.data);
    } catch (error) {
      console.error("Error fetching interviews:", error);
      toast.error("Failed to load interviews");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailTemplate = async () => {
    try {
      const response = await axios.get(`${API}/interview-email-template`, authHeaders());
      setEmailTemplate({
        subject: response.data.subject,
        body_html: response.data.body_html
      });
    } catch (error) {
      console.error("Error fetching email template:", error);
      toast.error("Failed to load email template");
    }
  };

  const handleAddInterview = () => {
    setSelectedInterview(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      position: "",
      interview_datetime: "",
      status: "scheduled",
      notes: ""
    });
    setShowAddModal(true);
  };

  const handleEditInterview = (interview) => {
    setSelectedInterview(interview);
    setFormData({
      name: interview.name,
      email: interview.email,
      phone: interview.phone || "",
      position: interview.position,
      interview_datetime: interview.interview_datetime?.slice(0, 16) || "",
      status: interview.status || "scheduled",
      notes: interview.notes || ""
    });
    setShowAddModal(true);
  };

  const handleViewInterview = (interview) => {
    setSelectedInterview(interview);
    setShowViewModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        data.append(key, value);
      });

      if (selectedInterview) {
        await axios.put(`${API}/interviews/${selectedInterview.id}`, data, authHeaders());
        toast.success("Interview updated successfully");
      } else {
        await axios.post(`${API}/interviews`, data, authHeaders());
        toast.success("Interview scheduled successfully");
      }

      setShowAddModal(false);
      fetchInterviews();
    } catch (error) {
      console.error("Error saving interview:", error);
      toast.error(error.response?.data?.detail || "Failed to save interview");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedInterview) return;
    
    try {
      await axios.delete(`${API}/interviews/${selectedInterview.id}`, authHeaders());
      toast.success("Interview deleted successfully");
      setShowDeleteConfirm(false);
      setSelectedInterview(null);
      fetchInterviews();
    } catch (error) {
      console.error("Error deleting interview:", error);
      toast.error("Failed to delete interview");
    }
  };

  const handleSendInvitation = async (interview) => {
    setSendingInvitation(interview.id);
    try {
      const response = await axios.post(
        `${API}/interviews/${interview.id}/send-invitation`,
        {},
        authHeaders()
      );
      
      if (response.data.email_sent) {
        toast.success("Interview invitation sent successfully!");
        fetchInterviews();
      } else {
        toast.error(response.data.message || "Failed to send invitation");
      }
    } catch (error) {
      console.error("Error sending invitation:", error);
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSendingInvitation(null);
    }
  };

  const handleOpenTemplateModal = async () => {
    await fetchEmailTemplate();
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      const data = new FormData();
      data.append("subject", emailTemplate.subject);
      data.append("body_html", emailTemplate.body_html);

      await axios.put(`${API}/interview-email-template`, data, authHeaders());
      toast.success("Email template saved successfully");
      setShowTemplateModal(false);
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save email template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetTemplate = async () => {
    try {
      await axios.post(`${API}/interview-email-template/reset`, {}, authHeaders());
      toast.success("Email template reset to default");
      await fetchEmailTemplate();
    } catch (error) {
      console.error("Error resetting template:", error);
      toast.error("Failed to reset template");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "scheduled": return "bg-blue-100 text-blue-700";
      case "invitation_sent": return "bg-amber-100 text-amber-700";
      case "completed": return "bg-green-100 text-green-700";
      case "cancelled": return "bg-red-100 text-red-700";
      case "no_show": return "bg-gray-100 text-gray-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "scheduled": return <Clock className="w-3 h-3" />;
      case "invitation_sent": return <Mail className="w-3 h-3" />;
      case "completed": return <CheckCircle className="w-3 h-3" />;
      case "cancelled": return <XCircle className="w-3 h-3" />;
      case "no_show": return <AlertCircle className="w-3 h-3" />;
      default: return null;
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    try {
      const date = new Date(isoString);
      return date.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true
      });
    } catch {
      return isoString;
    }
  };

  const filteredInterviews = interviews.filter(i => {
    const matchesSearch = 
      i.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.position.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || i.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: interviews.length,
    scheduled: interviews.filter(i => i.status === "scheduled").length,
    invitationSent: interviews.filter(i => i.status === "invitation_sent").length,
    completed: interviews.filter(i => i.status === "completed").length,
    cancelled: interviews.filter(i => i.status === "cancelled" || i.status === "no_show").length
  };

  return (
    <div className="p-6" data-testid="admin-interviews-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-[#1e3a8a]" />
            Interview Management
          </h1>
          <p className="text-slate-500 mt-1">Schedule and manage candidate interviews</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={handleOpenTemplateModal}
            data-testid="edit-template-btn"
          >
            <FileEdit className="w-4 h-4 mr-2" />
            Edit Email Template
          </Button>
          <Button 
            onClick={handleAddInterview}
            className="bg-[#1e3a8a] hover:bg-[#172554]"
            data-testid="add-interview-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Schedule Interview
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="border-0 shadow">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Scheduled</p>
            <p className="text-2xl font-bold text-blue-600">{stats.scheduled}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Invitation Sent</p>
            <p className="text-2xl font-bold text-amber-600">{stats.invitationSent}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Completed</p>
            <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Cancelled/No-Show</p>
            <p className="text-2xl font-bold text-red-600">{stats.cancelled}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email or position..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-interviews"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="invitation_sent">Invitation Sent</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="no_show">No Show</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchInterviews}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Interviews List */}
      <Card className="border-0 shadow">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#1e3a8a] border-t-transparent rounded-full" />
            </div>
          ) : filteredInterviews.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No interviews found</p>
              <Button 
                onClick={handleAddInterview} 
                className="mt-4 bg-[#1e3a8a] hover:bg-[#172554]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Schedule First Interview
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Candidate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Position</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Interview Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredInterviews.map((interview) => (
                    <tr key={interview.id} className="hover:bg-slate-50">
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-slate-900">{interview.name}</p>
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {interview.email}
                          </p>
                          {interview.phone && (
                            <p className="text-sm text-slate-400 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {interview.phone}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700">{interview.position}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-600">
                            {formatDateTime(interview.interview_datetime)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <Badge className={`${getStatusColor(interview.status)} flex items-center gap-1 w-fit`}>
                          {getStatusIcon(interview.status)}
                          {interview.status?.replace('_', ' ')}
                        </Badge>
                        {interview.invitation_sent && (
                          <p className="text-xs text-green-600 mt-1">
                            Invitation sent
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewInterview(interview)}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditInterview(interview)}
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {!interview.invitation_sent && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSendInvitation(interview)}
                              disabled={sendingInvitation === interview.id}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Send Invitation"
                              data-testid={`send-invitation-${interview.id}`}
                            >
                              {sendingInvitation === interview.id ? (
                                <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedInterview(interview);
                              setShowDeleteConfirm(true);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedInterview ? "Edit" : "Schedule"} Interview</DialogTitle>
            <DialogDescription>
              {selectedInterview ? "Update interview details" : "Schedule a new candidate interview"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Candidate Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Full name"
                  data-testid="interview-name"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="email@example.com"
                  data-testid="interview-email"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                />
              </div>
              <div className="col-span-2">
                <Label>Position *</Label>
                <Input
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  required
                  placeholder="e.g., Sales Executive"
                  data-testid="interview-position"
                />
              </div>
              <div className="col-span-2">
                <Label>Interview Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={formData.interview_datetime}
                  onChange={(e) => setFormData({ ...formData, interview_datetime: e.target.value })}
                  required
                  data-testid="interview-datetime"
                />
              </div>
              {selectedInterview && (
                <div className="col-span-2">
                  <Label>Status</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="invitation_sent">Invitation Sent</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="no_show">No Show</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-[#1e3a8a] hover:bg-[#172554]"
                data-testid="save-interview-btn"
              >
                {isSubmitting ? "Saving..." : selectedInterview ? "Update" : "Schedule"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Details Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Interview Details</DialogTitle>
          </DialogHeader>
          {selectedInterview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Candidate Name</p>
                  <p className="font-medium">{selectedInterview.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Position</p>
                  <p className="font-medium">{selectedInterview.position}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{selectedInterview.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-medium">{selectedInterview.phone || "-"}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-slate-500">Interview Date & Time</p>
                  <p className="font-medium">{formatDateTime(selectedInterview.interview_datetime)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={`${getStatusColor(selectedInterview.status)} mt-1`}>
                    {selectedInterview.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Invitation</p>
                  <p className="font-medium">
                    {selectedInterview.invitation_sent ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" /> Sent
                      </span>
                    ) : (
                      <span className="text-slate-400">Not sent</span>
                    )}
                  </p>
                </div>
                {selectedInterview.notes && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Notes</p>
                    <p className="text-slate-700">{selectedInterview.notes}</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowViewModal(false)}>
                  Close
                </Button>
                {!selectedInterview.invitation_sent && (
                  <Button 
                    onClick={() => {
                      setShowViewModal(false);
                      handleSendInvitation(selectedInterview);
                    }}
                    className="bg-[#1e3a8a] hover:bg-[#172554]"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Invitation
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Interview</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this interview for {selectedInterview?.name}? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Template Editor Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="w-5 h-5 text-[#1e3a8a]" />
              Edit Interview Invitation Email Template
            </DialogTitle>
            <DialogDescription>
              Customize the email that will be sent to candidates when you click &quot;Send Invitation&quot;
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Available Variables */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm font-medium text-blue-800 mb-2">Available Variables:</p>
              <div className="flex flex-wrap gap-2">
                {["{{candidate_name}}", "{{position}}", "{{interview_date}}", "{{interview_time}}", "{{company_address}}", "{{contact_email}}"].map(v => (
                  <code key={v} className="px-2 py-1 bg-white rounded text-xs text-blue-600 border border-blue-200">
                    {v}
                  </code>
                ))}
              </div>
            </div>

            <div>
              <Label>Email Subject</Label>
              <Input
                value={emailTemplate.subject}
                onChange={(e) => setEmailTemplate({ ...emailTemplate, subject: e.target.value })}
                placeholder="Interview Invitation - {{position}}"
                data-testid="template-subject"
              />
            </div>

            <div>
              <Label>Email Body (HTML)</Label>
              <Textarea
                value={emailTemplate.body_html}
                onChange={(e) => setEmailTemplate({ ...emailTemplate, body_html: e.target.value })}
                rows={15}
                className="font-mono text-sm"
                placeholder="<html>...</html>"
                data-testid="template-body"
              />
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={handleResetTemplate}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Reset to Default
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveTemplate}
                  disabled={savingTemplate}
                  className="bg-[#1e3a8a] hover:bg-[#172554]"
                  data-testid="save-template-btn"
                >
                  {savingTemplate ? "Saving..." : "Save Template"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInterviews;
